import * as React from 'react'
import * as d3 from "d3";
import './styles.scss'

interface Props {
    data: OfsNumbers[]
}

interface OfsNumbers {
    income: number,
    quintile: number,
    spendings: number,
    spent: number
}

const labels = {
    "99": "Vous",
    "5": "Les 20% les plus riches",
    "3": "Les revenus moyens",
    "1": "Les 20% les plus pauvres"
}
const margin = {top: 10, right: 10, bottom: 30, left: 10}

export default class ResultsChart extends React.Component<Props, any> {
    constructor(props:any) {
        super(props)
        this.drawChart = this.drawChart.bind(this)
        this.updateChart = this.updateChart.bind(this)
    }

    private drawChart() {
        // Set the size and margins
        this.width = this.node.parentNode.clientWidth - margin.left - margin.right
        this.height = this.width * (window.innerWidth > 800 ? 0.8 : 1.1) - margin.top - margin.bottom

        // Append the svg object to the body of the page
        this.svg = d3.select(this.node)
            .append("svg")
                .attr("width", this.width + margin.left + margin.right)
                .attr("height", this.height + margin.top + margin.bottom)
            .append("g")
                .attr("transform",
                    "translate(" + margin.left + "," + margin.top + ")")

        // Stripped pattern
        const defs = this.svg.append("defs")
        const hashPattern = defs.append("pattern")
            .attr('id', 'hash')
            .attr('width', 4)
            .attr('height', 8)
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('patternTransform', 'rotate(45)')
        hashPattern.append("rect")
            .attr('width', 4)
            .attr('height', 8)
            .attr('transform', 'translate(0,0)')
            .attr('fill', '#f5f6f7')
        hashPattern.append("rect")
            .attr('width', 1)
            .attr('height', 8)
            .attr('transform', 'translate(0,0)')
            .attr('fill', '#ad427f')

        // Dotted pattern
        const dotsPattern = defs.append("pattern")
            .attr('id', 'dot')
            .attr('width', 5)
            .attr('height', 5)
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('patternTransform', 'rotate(45)')
        dotsPattern.append("rect")
            .attr('width', 5)
            .attr('height', 5)
            .attr('transform', 'translate(0,0)')
            .attr('fill', '#f5f6f7')
        dotsPattern.append("rect")
            .attr('width', 1)
            .attr('height', 1)
            .attr('transform', 'translate(0,0)')
            .attr('fill', '#ad427f')

        // Find the max income (X axis limit)
        const maxIncome = d3.max(this.props.data, d => +d.income )
        
        // X axis
        this.x = d3.scaleLinear()
            .range([0, this.width ])
            .domain([0, 24000]) // Get the max with max on data instead

        // Y axis
        let y = d3.scaleBand()
            .range([this.height, 0])
            .domain(this.props.data.map(d => labels[d.quintile] ))
            .padding(0.2)

        // Draw the x Axis
        this.svg.append("g")
            .attr('class', 'xaxis')
            .attr("transform", "translate(0," + this.height + ")")
            .call(
                d3.axisBottom(this.x)
                .ticks(6)
                .tickFormat(d => {
                    let tick = (d===20000 ? '20 000 CHF' : d)
                    return tick
                }))

        // Draw the y Axis
        /* this.svg.append("g")
            .attr('class', 'yaxis')
            .call(d3.axisLeft(y)
                .tickSize([0, 0, 0])) */

        // Draw the grid
        this.svg.append('g')
            .attr('class', 'grid')
            .attr('transform', `translate(0, ${this.height})`)
            .call(d3.axisBottom()
                .scale(this.x)
                .ticks(6)
                .tickSize(-this.height, 0, 0))

        // Draw the user background
        /* this.svg
            .append("rect")
            .attr("class", 'background')
            .attr("y", 0 )
            .attr("x", 0 )
            .attr("height", y.bandwidth() * 1.25)
            .attr("width", this.width)
            .attr("fill", '#ad427f1c') */

        // Draw the income bars
        this.incomeBar = this.svg.selectAll("bar")
            .data(this.props.data)
            .enter()
            .append("rect")
            .attr("class", 'income')
            .attr("y", d => y(labels[d.quintile]) )
            .attr("x", 0 )
            .attr("height", y.bandwidth() * 0.75)
            .attr("width", 0)
            .attr("fill", d => { let color = d.quintile === 99 ? "#ad427f" : "#ad427f"; return color })

        // Draw the spendings bars
        this.spendingsBar = this.svg.selectAll("bar")
            .data(this.props.data)
            .enter()
            .append("rect")
            .attr("class", 'spendings')
            .attr("y", d => y(labels[d.quintile]) )
            .attr("height", y.bandwidth() * 0.75)
            .attr("width", 0 )
            .attr("fill", "url(#dot)")

        // Draw the spent bars
        this.spentBar = this.svg.selectAll("bar")
            .data(this.props.data)
            .enter()
            .append("rect")
            .attr("class", 'spent')
            .attr("x", 0 )
            .attr("y", d => y(labels[d.quintile]) )
            .attr("height", y.bandwidth() * 0.75)
            .attr("width", 0 )
            .attr("fill", "url(#hash)")

        // Draw the labels
        this.svg.selectAll(".text")        
            .data(this.props.data)
            .enter()
            .append("text")
            .attr("class", "label")
            .attr("x", 0)
            .attr("y", d => y(labels[d.quintile]) - 20 )
            .attr("dy", ".75em")
            .text(d => labels[d.quintile] )
    }

    private updateChart(){
        // Update the bars
        this.incomeBar
            .data(this.props.data)
            .transition()
                .attr("x", d => this.x(+d.spendings + +d.spent) )
                .attr("width", d => this.x(d.income - d.spendings - d.spent) )

        this.spendingsBar
            .data(this.props.data)
            .transition()
                .attr("width", d => this.x(+d.spendings) )

        this.spentBar
            .data(this.props.data)
            .transition()
                .attr("x", d => this.x(+d.spendings) )
                .attr("width", d => this.x(d.spent) )
    }

    public componentDidUpdate() {
        console.log('Chart Update')
        this.updateChart()
    }

    private updateDimensions() {
        let w = this.node.parentNode.clientWidth - margin.left - margin.right
        let h = w * 2 / 3 - margin.top - margin.bottom
        this.setState({
            width: w,
            height: h
        })
    }

    public componentDidMount() {
        this.drawChart()
        this.updateChart()

        window.addEventListener('resize', this.updateDimensions)
    }
    
    public componentWillUnmount() {
        window.removeEventListener("resize", this.updateDimensions);
    }

    public render() {
        return (
            <div className='ofsChart'>
                <div
                    ref={el => {this.node = el}}
                    className='ofsChartsContainer'>
                </div>
            </div>
        )
    }
}